import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { generateFeatureVoice } from '@/api/query/feature';
import { useLanguage, useLanguageData } from '@/common/hooks/useLanguageData';
import { MessageContext } from '@/common/contexts/message-context/MessageContext';
import { playAudioBase64 } from '@/common/functions/howlerAudio';
import {
	clearActiveMessageSpeech,
	registerActiveMessageSpeech,
	stopActiveMessageSpeech,
} from '../utils/messageSpeechManager';
import { CONVERSATION_STATS_SOURCE } from '../constants/chatRequests.constants';

export const MESSAGE_SPEECH_STATE = {
	IDLE: 'idle',
	LOADING: 'loading',
	SPEAKING: 'speaking',
};

const useMessageSpeech = ({ person, text, messageKey }) => {
	const t = useLanguageData('chat');
	const { lang } = useLanguage();
	const { addMessage } = useContext(MessageContext);
	const [speechState, setSpeechState] = useState(MESSAGE_SPEECH_STATE.IDLE);

	const stopRef = useRef(null);
	const isMountedRef = useRef(true);

	const stopSpeech = useCallback(() => {
		stopRef.current?.();
		stopRef.current = null;

		if (isMountedRef.current) {
			setSpeechState(MESSAGE_SPEECH_STATE.IDLE);
		}
	}, []);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			stopSpeech();
			clearActiveMessageSpeech(stopSpeech);
		};
	}, [stopSpeech, messageKey]);

	const handleSpeak = useCallback(async () => {
		const trimmedText = text?.trim();

		if (!trimmedText) {
			return;
		}

		if (speechState === MESSAGE_SPEECH_STATE.SPEAKING) {
			stopSpeech();
			clearActiveMessageSpeech(stopSpeech);
			return;
		}

		stopActiveMessageSpeech();
		registerActiveMessageSpeech(stopSpeech);
		setSpeechState(MESSAGE_SPEECH_STATE.LOADING);

		try {
			const { data } = await generateFeatureVoice({
				person: person?._id,
				message: trimmedText,
				type: 'CHAT',
				language: lang,
				stats_source: CONVERSATION_STATS_SOURCE.CHAT,
			});

			if (!isMountedRef.current) {
				return;
			}

			const mimeType = data?.mimeType || 'audio/mpeg';
			const handle = playAudioBase64(data.audio, mimeType);
			stopRef.current = handle.stop;

			if (isMountedRef.current) {
				setSpeechState(MESSAGE_SPEECH_STATE.SPEAKING);
			}

			await handle.promise;

			if (isMountedRef.current) {
				stopRef.current = null;
				setSpeechState(MESSAGE_SPEECH_STATE.IDLE);
				clearActiveMessageSpeech(stopSpeech);
			}
		} catch (error) {
			console.log(error);

			if (!isMountedRef.current) {
				return;
			}

			stopRef.current = null;
			setSpeechState(MESSAGE_SPEECH_STATE.IDLE);
			clearActiveMessageSpeech(stopSpeech);
			addMessage(t.speakError, 'error');
		}
	}, [
		addMessage,
		lang,
		person?._id,
		speechState,
		stopSpeech,
		t.speakError,
		text,
	]);

	return {
		speechState,
		handleSpeak,
		canSpeak: Boolean(text?.trim()),
	};
};

export default useMessageSpeech;
